import {
  getDashboardFederationsControllerCountQueryKey,
  getDashboardFederationsControllerListQueryKey,
} from "@/api/__generated__/dashboard-federations/dashboard-federations";
import { MessageKey } from "@/i18n";
import {
  FeeBasis,
  FeeType,
  LegalEntityRole,
  OrganizationGeoScope,
  OrganizationStatus,
  OrganizationSubscription,
  OrganizationType,
} from "@/types";

export const ORGANIZATIONS_INVALIDATE_KEYS = [
  getDashboardFederationsControllerListQueryKey(),
  getDashboardFederationsControllerCountQueryKey(),
];

export const ORGANIZATION_STATUS_LABELS: { [key in OrganizationStatus]: MessageKey } = {
  active: "organization.enums.status.active",
  inactive: "organization.enums.status.inactive",
};

// Tabs of the organization settings page (UpdateOrganizationView). Shared with
// global search so the current organization's tabs are searchable. Some tabs
// (fees, clubMembers, permissions) are conditionally shown by the page based on
// runtime org/platform data — search surfaces the full set gated only by edit
// permission, and the page falls back to "general" for a tab not currently visible.
export const UPDATE_ORGANIZATION_PAGE_TAB_IDS = [
  "general",
  "contacts",
  "legalDetails",
  "management",
  "paymentDetails",
  "about",
  "documents",
  "clubMembers",
  "permissions",
  "fees",
  "visibility",
  "extras",
] as const;

export type UpdateOrganizationPageTabId = (typeof UPDATE_ORGANIZATION_PAGE_TAB_IDS)[number];

export const UPDATE_ORGANIZATION_PAGE_TAB_LABELS: {
  [key in UpdateOrganizationPageTabId]: MessageKey;
} = {
  general: "organization.pages.updateOrganization.tabs.general",
  contacts: "organization.pages.updateOrganization.tabs.contacts",
  legalDetails: "organization.pages.updateOrganization.tabs.legalDetails",
  management: "organization.pages.updateOrganization.tabs.management",
  paymentDetails: "organization.pages.updateOrganization.tabs.paymentDetails",
  about: "organization.pages.updateOrganization.tabs.about",
  documents: "documents.title",
  clubMembers: "organization.pages.updateOrganization.tabs.clubMembers",
  permissions: "organization.pages.createOrganization.steps.permissions",
  fees: "organization.pages.updateOrganization.tabs.fees",
  visibility: "organization.pages.updateOrganization.tabs.visibility",
  extras: "organization.pages.createOrganization.steps.extras",
};

export const ORGANIZATION_TYPE_LABELS: { [key in OrganizationType]: MessageKey } = {
  governmentAuthority: "organization.enums.type.governmentAuthority",
  umbrellaSportsBody: "organization.enums.type.umbrellaSportsBody",
  nationalGoverningBody: "organization.enums.type.nationalGoverningBody",
  regionalGoverningBody: "organization.enums.type.regionalGoverningBody",
  internationalGoverningBody: "organization.enums.type.internationalGoverningBody",
  municipality: "organization.enums.type.municipality",
  clubOwner: "organization.enums.type.clubOwner",
  clubChainOwner: "organization.enums.type.clubChainOwner",
  privateEntity: "organization.enums.type.privateEntity",
  sportsClub: "organization.enums.type.sportsClub",
  school: "organization.enums.type.school",
  union: "organization.enums.type.union",
  sportsMinistry: "organization.enums.type.sportsMinistry",
  community: "organization.enums.type.community",
  educationalInstitution: "organization.enums.type.educationalInstitution",
};

export const GOVERNING_BODY_ORGANIZATION_TYPES: OrganizationType[] = [
  "internationalGoverningBody",
  "nationalGoverningBody",
  "regionalGoverningBody",
];

/** Whether any of the organization's types is a governing-body type (drives the club-members step/tab). */
export const hasGoverningBodyType = (types: OrganizationType[]): boolean =>
  types.some((type) => GOVERNING_BODY_ORGANIZATION_TYPES.includes(type));

export const ORGANIZATION_GEO_SCOPE_LABELS: { [key in OrganizationGeoScope]: MessageKey } = {
  international: "organization.enums.geoScope.international",
  national: "organization.enums.geoScope.national",
  local: "organization.enums.geoScope.local",
};

export const ORGANIZATION_SUBSCRIPTION_LABELS: {
  [key in OrganizationSubscription]: MessageKey;
} = {
  custom: "organization.enums.subscription.custom",
  customPlus: "organization.enums.subscription.customPlus",
  customUltra: "organization.enums.subscription.customUltra",
  marketplace: "organization.enums.subscription.marketplace",
};
export const LEGAL_ENTITY_ROLE_LABELS: {
  [key in LegalEntityRole]: MessageKey;
} = {
  president: "organization.enums.boardMemberRole.president",
  vicePresident: "organization.enums.boardMemberRole.vicePresident",
  boardMember: "organization.enums.boardMemberRole.boardMember",
  secretaryGeneral: "organization.enums.boardMemberRole.secretaryGeneral",
  chiefExecutiveOfficer: "organization.enums.boardMemberRole.chiefExecutiveOfficer",
  treasurer: "organization.enums.boardMemberRole.treasurer",
  executiveDirector: "organization.enums.boardMemberRole.executiveDirector",
  technicalDirector: "organization.enums.boardMemberRole.technicalDirector",
  operationsManager: "organization.enums.boardMemberRole.operationsManager",
  competitionManager: "organization.enums.boardMemberRole.competitionManager",
  committeeChair: "organization.enums.boardMemberRole.committeeChair",
  committeeMember: "organization.enums.boardMemberRole.committeeMember",
  athleteRepresentative: "organization.enums.boardMemberRole.athleteRepresentative",
  clubRepresentative: "organization.enums.boardMemberRole.clubRepresentative",
  generalAssemblyMember: "organization.enums.boardMemberRole.generalAssemblyMember",
  votingMember: "organization.enums.boardMemberRole.votingMember",
  officiatingManager: "organization.enums.boardMemberRole.officiatingManager",
  coachingManager: "organization.enums.boardMemberRole.coachingManager",
};

export const FEE_TYPE_LABELS: {
  [key in FeeType]: MessageKey;
} = {
  fixed: "organization.enums.feeType.fixed",
  percentage: "organization.enums.feeType.percentage",
};

export const FEE_BASIS_LABELS: {
  [key in FeeBasis]: MessageKey;
} = {
  perEntry: "organization.feeBasis.perEntry",
  perPlayer: "organization.feeBasis.perPlayer",
};
